import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UserCog } from 'lucide-react';
import { formatMoney, type PlatformUserDto, type RenewalRequestDto } from '@his/shared';
import { get, put, qs } from '@/lib/api';
import { dateOnly, dateTime } from '@/lib/utils';
import { statusLabel } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/layout';
import {
  Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Field, Select,
} from '@/components/ui/primitives';
import { OwnerConfirmDialog } from './OwnerConfirm';

/**
 * The commercial side of one hospital: who looks after it, and until when.
 *
 * Naming a contact person is what gives them sight of this hospital at all —
 * the client portal derives its entire reach from that one column — so it
 * carries the same step-up and audited reason as the other actions that change
 * who can see what.
 */
export function TenantAccountPanel({
  tenantId,
  contact,
  subscriptionEndsAt,
}: {
  tenantId: string;
  contact: { id: string; name: string; email: string; phone: string | null } | null;
  subscriptionEndsAt: string | null;
}) {
  const queryClient = useQueryClient();
  const [assigning, setAssigning] = useState(false);
  const [choice, setChoice] = useState(contact?.id ?? '');

  const contacts = useQuery({
    queryKey: ['platform-staff', 'contact_person'],
    queryFn: () => get<PlatformUserDto[]>('/platform/staff?role=contact_person'),
    enabled: assigning,
  });

  const renewals = useQuery({
    queryKey: ['owner-renewals', tenantId],
    queryFn: () => get<RenewalRequestDto[]>(`/platform/renewals${qs({ tenantId, limit: 20 })}`),
  });

  const daysLeft = subscriptionEndsAt
    ? Math.ceil((new Date(subscriptionEndsAt).getTime() - Date.now()) / 86_400_000)
    : null;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm">Contact person</CardTitle>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setChoice(contact?.id ?? '');
                setAssigning(true);
              }}
            >
              <UserCog className="me-1.5 h-4 w-4" /> {contact ? 'Change' : 'Assign'}
            </Button>
          </CardHeader>
          <CardContent>
            {contact ? (
              <div className="text-sm">
                <p className="font-medium">{contact.name}</p>
                <p className="text-muted-foreground">{contact.email}</p>
                {contact.phone && <p className="text-muted-foreground">{contact.phone}</p>}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Nobody is looking after this hospital. Its tickets reach only you, and nobody can
                send a renewal request for it.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Subscription</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            {subscriptionEndsAt ? (
              <>
                <p className="text-lg font-semibold">{dateOnly(subscriptionEndsAt)}</p>
                <p className="text-muted-foreground">
                  {daysLeft !== null && daysLeft >= 0 ? `${daysLeft} days left` : 'Expired'}
                </p>
              </>
            ) : (
              <p className="text-muted-foreground">
                No end date set. Access stays open until a renewal is approved with a date.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Alert>
        The nightly sweep drops a hospital to read-only when its period runs out. Records stay
        readable — billing is never the reason a clinician cannot open a chart.
      </Alert>

      <div>
        <h3 className="mb-2 text-sm font-semibold">Renewal history</h3>
        <DataTable
          rows={renewals.data}
          loading={renewals.isLoading} refreshing={renewals.isFetching}
          error={renewals.error}
          rowKey={(row) => row.id}
          empty="No renewal requests for this facility yet."
          columns={[
            { header: 'Sent', cell: (row) => dateTime(row.createdAt) },
            { header: 'By', cell: (row) => row.requestedByName },
            { header: 'Amount', align: 'right', cell: (row) => formatMoney(row.amountMinor, row.currency) },
            {
              header: 'Status',
              cell: (row) => (
                <Badge
                  variant={
                    row.status === 'approved' ? 'success' : row.status === 'rejected' ? 'destructive' : 'warning'
                  }
                >
                  {statusLabel(row.status, 'renewal_request_status')}
                </Badge>
              ),
            },
            { header: 'Runs to', cell: (row) => (row.approvedUntil ? dateOnly(row.approvedUntil) : '—') },
          ]}
        />
      </div>

      {assigning && (
        <OwnerConfirmDialog
          open
          onClose={() => setAssigning(false)}
          title="Name the contact person"
          description="They will see this facility in their portal, its tickets, and be able to request renewals for it."
          confirmLabel="Save contact"
          successMessage="Contact updated"
          action={({ totpCode, reason }) =>
            put(`/platform/tenants/${tenantId}/contact`, {
              totpCode,
              reason,
              contactUserId: choice || null,
            })
          }
          onDone={() => {
            setAssigning(false);
            void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
            void queryClient.invalidateQueries({ queryKey: ['platform-staff'] });
          }}
        >
          <Field label="Contact person" hint="Leave blank to remove the current one">
            <Select value={choice} onChange={(e) => setChoice(e.target.value)}>
              <option value="">Nobody</option>
              {(contacts.data ?? [])
                .filter((person) => person.isActive)
                .map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name} — {person.email}
                    {person.assignedTenants ? ` (${person.assignedTenants} hospitals)` : ''}
                  </option>
                ))}
            </Select>
          </Field>
        </OwnerConfirmDialog>
      )}
    </div>
  );
}
